export const VACCINE_PREVENTABLE_DISEASES = ['cholera', 'yellow fever', 'polio', 'measles', 'diphtheria'];

const COUNTRY_NAME_OVERLAPS = {
  congo: ['democratic republic of the congo', 'republic of the congo'],
  dominica: ['dominican republic'],
  guinea: ['guinea-bissau', 'guinea bissau', 'equatorial guinea', 'papua new guinea'],
  korea: ['north korea', 'south korea'],
  niger: ['nigeria'],
  samoa: ['american samoa'],
  sudan: ['south sudan']
};

const OUTBREAK_END_FIELD_NAMES = [
  'outbreak_end_reported',
  'outbreak_ended',
  'outbreak_end_date',
  'outbreak_end',
  'end_date',
  'date_ended',
  'ended_date'
];

const OUTBREAK_STATUS_FIELD_NAMES = ['status', 'outbreak_status', 'event_status'];
const OUTBREAK_DATE_FIELD_NAMES = [
  'outbreak_end',
  'outbreak_end_date',
  'outbreak_end_reported',
  'outbreak_last_reported',
  'outbreak_first_reported',
  'outbreak_start',
  'update_date',
  'publication_date',
  'reportDate'
];
const RESOLVED_OUTBREAK_MONTHS = 12;
const UNNAMED_OUTBREAK_DISEASES = ['undiagnosed'];
const UNNAMED_OUTBREAK_DISEASE_CODES = new Set(['12700']);

export const normalizeOutbreakText = (value = '') => String(value || '').trim().toLowerCase();

const isUnnamedOutbreakDisease = (item, diseaseName = '') => {
  const diseaseCode = String(item?.diseaseCode ?? item?.raw?.disease_code ?? '').trim();
  if (UNNAMED_OUTBREAK_DISEASE_CODES.has(diseaseCode)) return true;

  const normalizedDisease = normalizeOutbreakText(diseaseName || item?.disease);
  return UNNAMED_OUTBREAK_DISEASES.some((name) =>
    normalizedDisease === name || normalizedDisease.includes(name)
  );
};

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const hasStandaloneTerm = (text, term) => {
  const normalizedText = normalizeOutbreakText(text);
  const normalizedTerm = normalizeOutbreakText(term);
  if (!normalizedText || !normalizedTerm) return false;
  return new RegExp(`(^|[^a-z0-9])${escapeRegExp(normalizedTerm)}([^a-z0-9]|$)`).test(normalizedText);
};

const getOutbreakSearchText = (item) => [
  item?.title,
  item?.text,
  item?.disease,
  item?.country,
  item?.location,
  ...(Array.isArray(item?.bullets) ? item.bullets : [])
].filter(Boolean).join(' ').toLowerCase();

const diseaseMatchesItem = (item, disease) => {
  const normalizedDisease = normalizeOutbreakText(disease);
  const itemDisease = normalizeOutbreakText(item?.disease);
  if (itemDisease) {
    return itemDisease === normalizedDisease ||
      itemDisease.includes(normalizedDisease) ||
      normalizedDisease.includes(itemDisease);
  }
  return hasStandaloneTerm(getOutbreakSearchText(item), normalizedDisease);
};

const countryMatchesItem = (item, countryName, iso3 = '') => {
  const normalizedCountry = normalizeOutbreakText(countryName);
  const itemCountry = normalizeOutbreakText(item?.country);
  const itemCountryCode = normalizeOutbreakText(item?.countryCode || item?.raw?.country_code);
  const normalizedIso3 = normalizeOutbreakText(iso3);

  if (itemCountry && itemCountry === normalizedCountry) return true;
  if (normalizedIso3 && itemCountryCode && itemCountryCode === normalizedIso3) return true;

  // GIDEON already tags a country. Do not let article text light up similar names
  // (Niger/Nigeria, Sudan/South Sudan).
  if (itemCountry) return false;

  const searchText = getOutbreakSearchText(item);
  if (!hasStandaloneTerm(searchText, normalizedCountry)) return false;

  const overlaps = COUNTRY_NAME_OVERLAPS[normalizedCountry] || [];
  return overlaps.every((longerName) => {
    if (!hasStandaloneTerm(searchText, longerName)) return true;
    const stripped = searchText.replace(new RegExp(escapeRegExp(longerName), 'gi'), ' ');
    return hasStandaloneTerm(stripped, normalizedCountry);
  });
};

const getOutbreakField = (item, fieldNames) => {
  const source = item?.raw && typeof item.raw === 'object' ? item.raw : {};
  for (const fieldName of fieldNames) {
    const value = item?.[fieldName] ?? source?.[fieldName];
    if (value !== null && value !== undefined && String(value).trim() !== '') return value;
  }
  return '';
};

export const isOutbreakEnded = (item) => {
  if (getOutbreakField(item, OUTBREAK_END_FIELD_NAMES)) return true;
  const status = normalizeOutbreakText(getOutbreakField(item, OUTBREAK_STATUS_FIELD_NAMES));
  return Boolean(status && /(end|ended|resolved|closed|inactive|over)/i.test(status));
};

const parseOutbreakDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && value >= 1900 && value <= 2100) {
    return new Date(value, 11, 31);
  }

  const text = String(value).trim();
  if (/^\d{4}$/.test(text)) return new Date(Number(text), 11, 31);

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isOutbreakWithinLastMonths = (item, months = RESOLVED_OUTBREAK_MONTHS) => {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);

  const source = item?.raw && typeof item.raw === 'object' ? item.raw : {};
  const values = [
    ...OUTBREAK_DATE_FIELD_NAMES.map((fieldName) => item?.[fieldName] ?? source?.[fieldName]),
    item?.reportDate
  ];

  return values.some((value) => {
    const date = parseOutbreakDate(value);
    return Boolean(date && date >= cutoff);
  });
};

export const getDiseaseOutbreakItems = (countryName, disease, outbreakItems, iso3 = '') => {
  const normalizedCountry = normalizeOutbreakText(countryName);
  const normalizedDisease = normalizeOutbreakText(disease);
  if (!normalizedCountry || !normalizedDisease) return [];

  return (outbreakItems || []).filter((item) => {
    if (isOutbreakEnded(item)) return false;
    return countryMatchesItem(item, countryName, iso3) && diseaseMatchesItem(item, disease);
  });
};

export const getResolvedDiseaseOutbreakItems = (
  countryName,
  disease,
  outbreakItems,
  iso3 = '',
  months = RESOLVED_OUTBREAK_MONTHS,
  activeOutbreakItems = []
) => {
  const normalizedCountry = normalizeOutbreakText(countryName);
  const normalizedDisease = normalizeOutbreakText(disease);
  if (!normalizedCountry || !normalizedDisease) return [];

  const hasActiveOutbreak = getDiseaseOutbreakItems(countryName, disease, activeOutbreakItems, iso3).length > 0;
  const seen = new Set();

  return (outbreakItems || []).filter((item) => {
    if (!isOutbreakWithinLastMonths(item, months)) return false;
    if (!countryMatchesItem(item, countryName, iso3) || !diseaseMatchesItem(item, disease)) return false;
    if (!isOutbreakEnded(item) && hasActiveOutbreak) return false;

    const key = item?.id || `${item?.disease}-${item?.country}-${item?.reportDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getOtherOutbreakDiseaseNames = (
  countryName,
  outbreakItems,
  iso3 = '',
  excludedDiseases = VACCINE_PREVENTABLE_DISEASES
) => {
  const normalizedCountry = normalizeOutbreakText(countryName);
  if (!normalizedCountry) return [];

  const diseaseNames = new Map();

  (outbreakItems || []).forEach((item) => {
    if (isOutbreakEnded(item)) return;
    if (!countryMatchesItem(item, countryName, iso3)) return;
    if (excludedDiseases.some((disease) => diseaseMatchesItem(item, disease))) return;

    const diseaseName = String(item?.disease || '').trim();
    const normalizedDisease = normalizeOutbreakText(diseaseName);
    if (!normalizedDisease || isUnnamedOutbreakDisease(item, diseaseName) || diseaseNames.has(normalizedDisease)) return;

    diseaseNames.set(normalizedDisease, diseaseName);
  });

  return Array.from(diseaseNames.values()).sort((a, b) => a.localeCompare(b));
};

export const toOutbreakCountry = (row = {}, countryName = '') => ({
  countryName: String(countryName || row.countryName || row.Country_Name || row.country_name || '').trim(),
  iso3: String(row.iso3 || row.ISO3 || '').trim()
});

export const countActiveOutbreakCells = (
  alertItems,
  countries,
  diseases = VACCINE_PREVENTABLE_DISEASES
) => {
  const uniqueCountries = [];
  const seenNames = new Set();

  (countries || []).forEach((country) => {
    const countryName = String(country?.countryName || '').trim();
    if (!countryName || seenNames.has(countryName)) return;
    seenNames.add(countryName);
    uniqueCountries.push({
      countryName,
      iso3: String(country?.iso3 || '').trim()
    });
  });

  const countryNames = new Set(
    uniqueCountries.map((country) => normalizeOutbreakText(country.countryName)).filter(Boolean)
  );

  const filteredAlerts = (alertItems || []).filter((item) =>
    countryNames.has(normalizeOutbreakText(item?.country)) &&
    !isOutbreakEnded(item)
  );

  return uniqueCountries.reduce((count, country) => (
    count + diseases.filter((disease) =>
      getDiseaseOutbreakItems(country.countryName, disease, filteredAlerts, country.iso3).length > 0
    ).length
  ), 0);
};
